import {
  type RepriceApprovalKind,
  type RepriceApprovalListItem,
  type RepriceApprovalStatus,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzDialog, UzTextarea } from '@utazzo/ui';
import { Check, ExternalLink, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fmtMoney } from '../components/price-visibility';
import { useApproveReprice, useRejectReprice, useRepriceApprovals } from '../use-reprice-approvals';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useDataGridState } from '@/hooks/use-data-grid-state';

/**
 * Dedicated itinerary-reprice approval queue (Task 7). A >2%-of-margin
 * discount reprice (Task 6) writes a PENDING row here, routed through the
 * lead reviewer hierarchy. Separate from `/leads/approvals` (LOST/Won/
 * destination/status-change) - same reviewer-scoping shape (ADMIN sees
 * everything, SALES_LEAD sees rows assigned to them, SALES_AGENT sees only
 * their own submissions, read-only), server-enforced via `GET
 * /itinerary-reprice-approvals`.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'SUPERSEDED', label: 'Superseded' },
] as const;

const KIND_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'DISCOUNT', label: 'Discount' },
  { value: 'INSTALMENT_SPLIT', label: 'Installment split' },
  { value: 'INSTALMENT_DATE_CHANGE', label: 'Installment dates' },
] as const;

/** Opens on the "Pending" slice - the rows a reviewer actually has to action. */
const DEFAULT_FILTERS: Readonly<Record<string, string>> = { status: 'PENDING' };

const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'kind',
    label: 'Type',
    options: KIND_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-36',
  },
];

const parseStatusFilter = (value: string | undefined): RepriceApprovalStatus | undefined => {
  if (
    value === 'PENDING' ||
    value === 'APPROVED' ||
    value === 'REJECTED' ||
    value === 'SUPERSEDED'
  ) {
    return value;
  }
  return undefined;
};

const parseKindFilter = (value: string | undefined): RepriceApprovalKind | undefined => {
  if (value === 'DISCOUNT' || value === 'INSTALMENT_SPLIT' || value === 'INSTALMENT_DATE_CHANGE') {
    return value;
  }
  return undefined;
};

const kindLabel = (kind: RepriceApprovalKind): string =>
  kind === 'INSTALMENT_SPLIT'
    ? 'Installment split'
    : kind === 'INSTALMENT_DATE_CHANGE'
      ? 'Installment dates'
      : 'Discount';

/** Money formatter that tolerates the nullable discount fields (null -> "-"). */
const money = (paise: string | null): string => (paise ? (fmtMoney(paise) ?? '-') : '-');

/** For an INSTALMENT_SPLIT row: the balance moved out of instalment 1. */
const splitShortfallLabel = (row: RepriceApprovalListItem): string => {
  const s = row.instalmentSplit;
  if (!s) return '-';
  try {
    return money((BigInt(s.originalI1Paise) - BigInt(s.i1Paise)).toString());
  } catch {
    return '-';
  }
};

/** For an INSTALMENT_DATE_CHANGE row: how many instalment dates move. */
const dateChangeCountLabel = (row: RepriceApprovalListItem): string => {
  const n = row.instalmentDateChange?.changes.length ?? 0;
  return n === 0 ? '-' : `${n} date${n > 1 ? 's' : ''}`;
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Mirrors the server's `assertCanReviewRepriceApproval`: ADMIN reviews any
 *  row; the assigned SALES_LEAD reviews their own; everyone else (including
 *  the requesting SALES_AGENT) is read-only. */
const canReviewRow = (
  row: RepriceApprovalListItem,
  me: { id: string; role: string } | null | undefined,
): boolean => {
  if (!me) return false;
  if (me.role === 'ADMIN') return true;
  if (me.role === 'SALES_LEAD' && row.assignedReviewer?.id === me.id) return true;
  return false;
};

export const RepriceApprovalsPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const isAdmin = me?.role === 'ADMIN';
  const isReviewer = me?.role === 'ADMIN' || me?.role === 'SALES_LEAD';

  const gridState = useDataGridState({
    filterIds: ['kind', 'status'],
    defaultFilters: DEFAULT_FILTERS,
  });
  const { state } = gridState;
  const statusFilter = parseStatusFilter(state.filters.status as string | undefined);
  const kindFilter = parseKindFilter(state.filters.kind as string | undefined);
  const searchTerm = state.q.trim() || undefined;

  const listQuery = useRepriceApprovals({
    page: state.page,
    pageSize: state.pageSize,
    status: statusFilter,
    kind: kindFilter,
    search: searchTerm,
    sortDir: state.sortDir,
  });
  const approve = useApproveReprice();
  const reject = useRejectReprice();

  const [dialogTarget, setDialogTarget] = useState<{
    row: RepriceApprovalListItem;
    action: 'approve' | 'reject';
  } | null>(null);
  const [remark, setRemark] = useState('');

  const closeDialog = (): void => {
    setDialogTarget(null);
    setRemark('');
  };

  const submitting = approve.isPending || reject.isPending;
  const remarkRequired = dialogTarget?.action === 'reject';
  const remarkTrimmed = remark.trim();
  const canSubmit = !remarkRequired || remarkTrimmed.length > 0;

  const submitReview = async (): Promise<void> => {
    if (!dialogTarget || !canSubmit) return;
    try {
      if (dialogTarget.action === 'approve') {
        await approve.mutateAsync({
          id: dialogTarget.row.id,
          remark: remarkTrimmed || undefined,
        });
      } else {
        await reject.mutateAsync({ id: dialogTarget.row.id, remark: remarkTrimmed });
      }
      closeDialog();
    } catch {
      // Error toast already shown by the mutation hooks.
    }
  };

  const rows = listQuery.data?.data ?? [];
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<RepriceApprovalListItem>> => {
    const cols: DataGridColumn<RepriceApprovalListItem>[] = [
      {
        id: 'itinerary',
        header: 'Itinerary / lead',
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">
              {row.itineraryTitle ?? 'Untitled itinerary'}
            </span>
            <span className="text-xs text-zinc-500">
              {row.leadReference ?? 'No lead'}
              {row.customerName ? ` · ${row.customerName}` : ''}
            </span>
          </div>
        ),
      },
      {
        id: 'kind',
        header: 'Type',
        width: '140px',
        cell: (row) => (
          <UzBadge tone={row.kind === 'DISCOUNT' ? 'default' : 'secondary'} pill>
            {kindLabel(row.kind)}
          </UzBadge>
        ),
      },
      {
        id: 'requestedBy',
        header: 'Requested by',
        width: '160px',
        cell: (row) => <span className="text-sm text-zinc-700">{row.requestedBy.fullName}</span>,
      },
      {
        id: 'assignedReviewer',
        header: 'Requested to',
        width: '160px',
        cell: (row) => (
          <span className="text-sm text-zinc-700">
            {row.assignedReviewer ? (
              row.assignedReviewer.fullName
            ) : (
              <span className="text-zinc-500">Admin (any)</span>
            )}
          </span>
        ),
      },
      {
        id: 'originalGrandPaise',
        header: 'Original',
        width: '130px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">
            {row.kind === 'INSTALMENT_SPLIT'
              ? money(row.instalmentSplit?.originalI1Paise ?? null)
              : row.kind === 'INSTALMENT_DATE_CHANGE'
                ? '-'
                : money(row.originalGrandPaise)}
          </span>
        ),
      },
      {
        id: 'achievedGrandPaise',
        header: 'New',
        width: '130px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">
            {row.kind === 'INSTALMENT_SPLIT'
              ? money(row.instalmentSplit?.i1Paise ?? null)
              : row.kind === 'INSTALMENT_DATE_CHANGE'
                ? '-'
                : money(row.achievedGrandPaise)}
          </span>
        ),
      },
      {
        id: 'amountPaise',
        header: 'Discount / balance moved',
        width: '150px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">
            {row.kind === 'INSTALMENT_SPLIT'
              ? splitShortfallLabel(row)
              : row.kind === 'INSTALMENT_DATE_CHANGE'
                ? dateChangeCountLabel(row)
                : money(row.amountPaise)}
          </span>
        ),
      },
      // The discount percentage + the markup columns expose margin - ADMIN only.
      // Reviewers approve on the discount amount + pending status; non-admins
      // never see the % or the markup.
      ...(isAdmin
        ? [
            {
              id: 'marginPct',
              header: 'Discount % (of selling price)',
              width: '90px',
              cell: (row) => (
                <span className="text-sm font-medium text-amber-700 tabular-nums">
                  {row.marginPct != null ? `${row.marginPct.toFixed(2)}%` : '-'}
                </span>
              ),
            } as DataGridColumn<RepriceApprovalListItem>,
            // Markup on the pre-tax net cost, before vs after this reprice
            // (new = markup - discount + upsell). Shows what the margin drops to.
            {
              id: 'currentMarkupPct',
              header: 'Current markup %',
              width: '120px',
              cell: (row) => (
                <span className="text-sm text-zinc-700 tabular-nums">
                  {row.currentMarkupPct != null ? `${row.currentMarkupPct.toFixed(2)}%` : '-'}
                </span>
              ),
            } as DataGridColumn<RepriceApprovalListItem>,
            {
              id: 'newMarkupPct',
              header: 'New markup %',
              width: '110px',
              cell: (row) => (
                <span className="text-sm font-medium text-emerald-700 tabular-nums">
                  {row.newMarkupPct != null ? `${row.newMarkupPct.toFixed(2)}%` : '-'}
                </span>
              ),
            } as DataGridColumn<RepriceApprovalListItem>,
          ]
        : []),
      {
        id: 'createdAt',
        header: 'Requested',
        width: '140px',
        cell: (row) => <span className="text-sm text-zinc-600">{formatWhen(row.createdAt)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '110px',
        cell: (row) => {
          const tone: 'success' | 'destructive' | 'secondary' =
            row.status === 'APPROVED'
              ? 'success'
              : row.status === 'REJECTED'
                ? 'destructive'
                : 'secondary';
          return (
            <UzBadge tone={tone} pill>
              {row.status}
            </UzBadge>
          );
        },
      },
    ];

    cols.push({
      id: 'actions',
      header: '',
      width: '220px',
      align: 'right',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <UzButton
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/itineraries/${row.itineraryId}`)}
            title="Open itinerary"
          >
            <ExternalLink className="h-4 w-4" />
          </UzButton>
          {row.status === 'PENDING' && canReviewRow(row, me) && (
            <>
              <UzButton size="sm" onClick={() => setDialogTarget({ row, action: 'approve' })}>
                <Check className="mr-1 h-4 w-4" />
                Approve
              </UzButton>
              <UzButton
                size="sm"
                variant="outline"
                onClick={() => setDialogTarget({ row, action: 'reject' })}
              >
                <X className="mr-1 h-4 w-4" />
                Reject
              </UzButton>
            </>
          )}
        </div>
      ),
    });

    return cols;
  }, [me, navigate]);

  const description = isAdmin
    ? 'Every itinerary reprice that needs approval - pending, approved and rejected. Approve or reject the ones still pending.'
    : isReviewer
      ? 'Reprices submitted by your direct reports that need approval. Approve or reject the ones still pending.'
      : 'The reprice requests you have submitted - pending, approved and rejected.';

  const emptyTitle = searchTerm
    ? `No reprice approvals match "${state.q.trim()}"`
    : isReviewer
      ? 'No reprice approvals'
      : 'You have no reprice approval requests';
  const emptyDescription = searchTerm
    ? 'Search covers the itinerary title, lead reference, customer name and requester.'
    : 'A request appears here when a discount reprice needs approval. Smaller discounts publish directly, no approval needed.';

  return (
    <PageShell title="Reprice approvals" description={description}>
      <DataGrid
        gridId="itinerary-reprice-approvals"
        data={rows}
        columns={columns}
        filters={STATIC_FILTERS}
        getRowId={(row) => row.id}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        onRowClick={(row) => navigate(`/itineraries/${row.itineraryId}`)}
        searchPlaceholder="Search itinerary, lead ref or requester"
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        tableLayout="auto"
      />

      <UzDialog
        open={dialogTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        title={`${dialogTarget?.action === 'approve' ? 'Approve' : 'Reject'} reprice for ${
          dialogTarget?.row.itineraryTitle ?? 'this itinerary'
        }`}
        description={
          dialogTarget?.row.kind === 'INSTALMENT_SPLIT'
            ? dialogTarget?.action === 'approve'
              ? 'Approving applies the new installment split and regenerates the customer payment links. Until now the links kept the original amounts.'
              : 'Rejecting discards the proposed installment split. The payment plan and links are left unchanged. A remark is required so the seller knows why.'
            : dialogTarget?.row.kind === 'INSTALMENT_DATE_CHANGE'
              ? dialogTarget?.action === 'approve'
                ? 'Approving applies the installment date / amount changes. The plan total is unchanged; links for any amount that changed are regenerated.'
                : 'Rejecting discards the proposed installment changes. The payment plan is left unchanged. A remark is required so the seller knows why.'
              : dialogTarget?.action === 'approve'
                ? 'Approving unblocks publish - it does not publish the itinerary itself. The seller still has to send it.'
                : 'Rejecting clears the reprice: the solved discount is zeroed and the itinerary returns to its pre-reprice quote. A remark is required so the seller knows why.'
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={closeDialog} disabled={submitting}>
              Cancel
            </UzButton>
            <UzButton
              variant={dialogTarget?.action === 'reject' ? 'outline' : 'default'}
              onClick={() => void submitReview()}
              loading={submitting}
              disabled={!canSubmit}
            >
              {dialogTarget?.action === 'approve' ? 'Confirm approval' : 'Confirm rejection'}
            </UzButton>
          </div>
        }
      >
        {dialogTarget && (
          <div className="space-y-3 text-sm text-zinc-700">
            {dialogTarget.row.kind === 'INSTALMENT_SPLIT' && dialogTarget.row.instalmentSplit ? (
              <div className="space-y-1">
                <p>
                  First installment{' '}
                  <strong>{money(dialogTarget.row.instalmentSplit.originalI1Paise)}</strong> →{' '}
                  <strong>{money(dialogTarget.row.instalmentSplit.i1Paise)}</strong>.
                </p>
                {dialogTarget.row.instalmentSplit.mode === 'NEW' ? (
                  <p>
                    Balance <strong>{splitShortfallLabel(dialogTarget.row)}</strong> becomes a new
                    installment
                    {dialogTarget.row.instalmentSplit.newInstalmentDueDate ? ' due ' : ''}
                    {dialogTarget.row.instalmentSplit.newInstalmentDueDate ? (
                      <strong>{dialogTarget.row.instalmentSplit.newInstalmentDueDate}</strong>
                    ) : null}
                    .
                  </p>
                ) : (
                  <p>
                    Balance <strong>{splitShortfallLabel(dialogTarget.row)}</strong> moves into the
                    second installment{' '}
                    <strong>{money(dialogTarget.row.instalmentSplit.originalI2Paise)}</strong> →{' '}
                    <strong>{money(dialogTarget.row.instalmentSplit.i2Paise)}</strong>, due{' '}
                    <strong>{dialogTarget.row.instalmentSplit.originalI2DueDate}</strong> →{' '}
                    <strong>{dialogTarget.row.instalmentSplit.i2DueDate}</strong> (one day earlier).
                  </p>
                )}
              </div>
            ) : dialogTarget.row.kind === 'INSTALMENT_DATE_CHANGE' &&
              dialogTarget.row.instalmentDateChange ? (
              <div className="space-y-1">
                <p className="font-medium text-zinc-900">Installment changes</p>
                <ul className="list-disc space-y-0.5 pl-5">
                  {dialogTarget.row.instalmentDateChange.changes.map((c) => (
                    <li key={c.instalmentId}>
                      Due <strong>{c.oldDueDate}</strong> → <strong>{c.newDueDate}</strong>
                      {c.oldAmountPaise !== null && c.newAmountPaise !== null ? (
                        <>
                          {' '}
                          · amount <strong>{money(c.oldAmountPaise)}</strong> →{' '}
                          <strong>{money(c.newAmountPaise)}</strong>
                        </>
                      ) : null}
                    </li>
                  ))}
                  {dialogTarget.row.instalmentDateChange.newInstalment ? (
                    <li>
                      New installment{' '}
                      <strong>
                        {money(dialogTarget.row.instalmentDateChange.newInstalment.amountPaise)}
                      </strong>{' '}
                      due{' '}
                      <strong>{dialogTarget.row.instalmentDateChange.newInstalment.dueDate}</strong>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : (
              <p>
                From <strong>{money(dialogTarget.row.originalGrandPaise)}</strong> to{' '}
                <strong>{money(dialogTarget.row.achievedGrandPaise)}</strong> - a discount of{' '}
                <strong>{money(dialogTarget.row.amountPaise)}</strong>
                {isAdmin && dialogTarget.row.marginPct != null
                  ? ` (${dialogTarget.row.marginPct.toFixed(2)}% of the selling price)`
                  : ''}
                .
              </p>
            )}
            <p>
              Requested by <strong>{dialogTarget.row.requestedBy.fullName}</strong>
            </p>
            <UzTextarea
              name="reviewRemark"
              label={remarkRequired ? 'Reason for rejection (required)' : 'Remark (optional)'}
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              disabled={submitting}
            />
          </div>
        )}
      </UzDialog>
    </PageShell>
  );
};
